import { Modal } from 'antd'

/** Wait after the action before moving focus, so antd has closed the dialog. */
const AFTER_CLOSE_DELAY_MS = 0

export const isFocusReturnTarget = element =>
  Boolean(
    element &&
      typeof element.focus === 'function' &&
      element.isConnected !== false &&
      element !== document.body &&
      !element.disabled,
  )

const resolveFallback = fallback =>
  typeof fallback === 'function' ? fallback() : fallback

/**
 * Put focus back on the control that opened a dialog. When that control is
 * gone (the row it lived on was archived / reloaded), use the fallback.
 * @returns {Element|null} what got focus
 */
export const returnFocusAfterDialog = (opener, fallback = null) => {
  const target = isFocusReturnTarget(opener)
    ? opener
    : resolveFallback(fallback)
  if (!isFocusReturnTarget(target)) return null
  target.focus()
  return target
}

/**
 * `Modal.confirm` that returns focus to its opener. antd already does this
 * when the opener still exists; a successful OK often removes it (Skip today,
 * Archive), so focus then goes to `fallbackFocus` instead of the page body.
 */
export const confirmWithFocusReturn = (
  config,
  { fallbackFocus = null } = {},
) => {
  const opener = typeof document !== 'undefined' ? document.activeElement : null
  const afterClose = () =>
    setTimeout(
      () => returnFocusAfterDialog(opener, fallbackFocus),
      AFTER_CLOSE_DELAY_MS,
    )
  const wrap =
    handler =>
    async (...args) => {
      // A rejected OK keeps the dialog open — leave focus inside it.
      const result = await handler?.(...args)
      afterClose()
      return result
    }
  return Modal.confirm({
    ...config,
    onOk: wrap(config.onOk),
    onCancel: wrap(config.onCancel),
  })
}
